import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Spin, Tooltip } from 'antd';
import { ClockCircleOutlined, DownOutlined, ExportOutlined, FilterOutlined, LoadingOutlined, UpOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from '@umijs/max';
import { getAgentConversationFacets, getAgentConversationNav, getAgentConversationStatus } from '@/services/assets/api';
import type { HistoryMenuFacetNode, SessionHistoryItem } from './types';
import { ALL_FACET_KEY, buildHistoryMenuFacetTree, findFacetNodeByKey, flattenFacetTree, getNavFilterParamsFromFacetNode, getPendingSessionIdsFromHistoryItems, getPendingSessionIdsFromStatusItems, mergeConversationStatusItems } from './util';
import HistoryDeleteModal from './components/historyDeleteModal';
import HistoryList from './components/historyList';
import HistoryRenameModal from './components/historyRenameModal';
import useHistoryItemActions from '../../hooks/useHistoryItemActions';
import useHistoryConversationSync from '../../hooks/useHistoryConversationSync';
import useAgentConversationCursorList from '../../hooks/useAgentConversationCursorList';
import { useHistoryConversationStore } from '@/stores/history-conversation';
import './index.less';

const STATUS_POLLING_INTERVAL = 60000;
const SIDEBAR_LOAD_MORE_THRESHOLD_PX = 60;

interface HistoryMenuFooterProps {
  collapsed?: boolean;
}

function renderFacetIcon(icon?: string) {
  if (!icon) {
    return undefined;
  }
  if (icon.startsWith('http://') || icon.startsWith('https://') || icon.startsWith('data:')) {
    return <img src={icon} alt="" width={16} height={16} />;
  }
  return icon;
}

const HistoryMenuFooter: React.FC<HistoryMenuFooterProps> = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAllProjectsActive = location.pathname.startsWith('/session-history');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const statusPollingTimerRef = useRef<number | null>(null);
  const statusPollingTokenRef = useRef(0);
  const pendingSessionIdsRef = useRef<Set<string>>(new Set());
  const statusPollingInFlightRef = useRef(false);
  const pendingImmediatePollRef = useRef(false);
  const setItemsRef = useRef<(updater: React.SetStateAction<SessionHistoryItem[]>) => void>(() => undefined);
  const [expanded, setExpanded] = useState(true);
  const [selectedKey, setSelectedKey] = useState(ALL_FACET_KEY);
  const [facetTree, setFacetTree] = useState<HistoryMenuFacetNode[]>(() => buildHistoryMenuFacetTree());

  const selectedNode = useMemo(() => findFacetNodeByKey(facetTree, selectedKey), [facetTree, selectedKey]);

  const navFilterParams = useMemo(() => getNavFilterParamsFromFacetNode(selectedNode ?? { key: ALL_FACET_KEY }), [selectedKey, selectedNode?.agent_family, selectedNode?.agent]);

  const filterOptions = useMemo(() => {
    const flatNodes = flattenFacetTree(facetTree);
    return flatNodes.map((node) => ({
      key: node.key,
      icon: renderFacetIcon(node.icon),
      label: node.label,
      extra: node.count,
      ...(node.depth > 0 ? { style: { paddingInlineStart: 16 + node.depth * 16 } } : {}),
    }));
  }, [facetTree]);

  const clearStatusPolling = useCallback(() => {
    statusPollingTokenRef.current += 1;
    statusPollingInFlightRef.current = false;
    pendingImmediatePollRef.current = false;
    pendingSessionIdsRef.current.clear();
    if (statusPollingTimerRef.current) {
      window.clearTimeout(statusPollingTimerRef.current);
      statusPollingTimerRef.current = null;
    }
  }, []);

  const pollConversationStatusRef = useRef<(token: number) => Promise<void>>(async () => undefined);

  const scheduleStatusPolling = useCallback((token: number) => {
    if (statusPollingTokenRef.current !== token) {
      return;
    }
    if (!pendingSessionIdsRef.current.size) {
      statusPollingTimerRef.current = null;
      return;
    }
    if (statusPollingTimerRef.current || statusPollingInFlightRef.current) {
      return;
    }

    statusPollingTimerRef.current = window.setTimeout(() => {
      statusPollingTimerRef.current = null;
      void pollConversationStatusRef.current(token);
    }, STATUS_POLLING_INTERVAL);
  }, []);

  const pollConversationStatus = useCallback(
    async (token: number) => {
      const sessionIds = Array.from(pendingSessionIdsRef.current);
      if (!sessionIds.length || statusPollingTokenRef.current !== token) {
        return;
      }

      statusPollingInFlightRef.current = true;
      try {
        const res = await getAgentConversationStatus({ session_ids: sessionIds });
        if (res?.code !== 200) {
          throw new Error(res?.msg || '会话状态加载失败');
        }
        if (statusPollingTokenRef.current !== token) {
          return;
        }

        const statusItems = res?.data?.items || [];
        setItemsRef.current((previous) => mergeConversationStatusItems(previous, statusItems));

        const pendingFromStatus = new Set(getPendingSessionIdsFromStatusItems(statusItems));
        const returnedIds = new Set(statusItems.map((item) => item.session_id).filter(Boolean));

        // 仅移除「本次已返回且已非 pending」的 id，保留中途 enqueue 的新 id
        sessionIds.forEach((sessionId) => {
          if (returnedIds.has(sessionId) && !pendingFromStatus.has(sessionId)) {
            pendingSessionIdsRef.current.delete(sessionId);
          }
        });
      } catch (error) {
        if (statusPollingTokenRef.current !== token) {
          return;
        }
      } finally {
        statusPollingInFlightRef.current = false;
        let shouldPollImmediately = false;
        if (pendingImmediatePollRef.current) {
          pendingImmediatePollRef.current = false;
          shouldPollImmediately = statusPollingTokenRef.current === token && pendingSessionIdsRef.current.size > 0;
        }
        if (shouldPollImmediately) {
          void pollConversationStatusRef.current(token);
        } else {
          scheduleStatusPolling(token);
        }
      }
    },
    [scheduleStatusPolling],
  );

  pollConversationStatusRef.current = pollConversationStatus;

  const ensureStatusPolling = useCallback(() => {
    if (!pendingSessionIdsRef.current.size) {
      return;
    }
    const token = statusPollingTokenRef.current;
    if (statusPollingTimerRef.current || statusPollingInFlightRef.current) {
      return;
    }
    void pollConversationStatus(token);
  }, [pollConversationStatus]);

  /** 增量追加：新建会话时合并进现有轮询集合，并尽快带上新 id 发起 status */
  const enqueueStatusPolling = useCallback(
    (sessionIds: string[]) => {
      let added = false;
      sessionIds.forEach((sessionId) => {
        const nextId = sessionId?.trim();
        if (!nextId) {
          return;
        }
        if (!pendingSessionIdsRef.current.has(nextId)) {
          pendingSessionIdsRef.current.add(nextId);
          added = true;
        }
      });

      if (!pendingSessionIdsRef.current.size) {
        return;
      }

      // 有新增 id 时，清掉等待中的 timer，尽快用完整 Set 打一轮 status
      if (added && statusPollingTimerRef.current) {
        window.clearTimeout(statusPollingTimerRef.current);
        statusPollingTimerRef.current = null;
      }

      if (statusPollingInFlightRef.current) {
        // 当前请求已带旧快照发出，结束后立刻再用完整 Set 补一轮
        if (added) {
          pendingImmediatePollRef.current = true;
        }
        return;
      }

      void pollConversationStatus(statusPollingTokenRef.current);
    },
    [pollConversationStatus],
  );

  /** 全量重置：仅在初始化 / reload 后覆盖轮询集合；loadMore 只做增量合并 */
  const startStatusPolling = useCallback(
    (nextItems: SessionHistoryItem[], meta?: { isReset: boolean }) => {
      const pendingSessionIds = getPendingSessionIdsFromHistoryItems(nextItems);
      if (meta?.isReset !== false) {
        clearStatusPolling();
        pendingSessionIds.forEach((sessionId) => pendingSessionIdsRef.current.add(sessionId));
        ensureStatusPolling();
        return;
      }

      enqueueStatusPolling(pendingSessionIds);
    },
    [clearStatusPolling, enqueueStatusPolling, ensureStatusPolling],
  );

  const { items, setItems, loading, loadingMore, hasMore, reload, handleScroll, bindScrollContainer, loadMoreSentinelRef } = useAgentConversationCursorList({
    fetcher: getAgentConversationNav,
    params: navFilterParams,
    observerRoot: 'container',
    loadMoreThresholdPx: SIDEBAR_LOAD_MORE_THRESHOLD_PX,
    errorMessage: '历史记录加载失败',
    loadMoreErrorMessage: '加载更多历史记录失败',
    onListLoaded: startStatusPolling,
    onResetError: clearStatusPolling,
  });

  setItemsRef.current = setItems;

  // 滚动容器已提升到「菜单 + 项目」外层，加载更多需绑定同一层，避免列表独立滚动失效
  useEffect(() => {
    if (collapsed) {
      bindScrollContainer(null);
      return;
    }

    const scrollRoot = rootRef.current?.closest('.session-history-sider-scroll') as HTMLDivElement | null;
    if (!scrollRoot) {
      bindScrollContainer(null);
      return;
    }

    bindScrollContainer(scrollRoot);
    const onScroll = (event: Event) => {
      handleScroll(event as unknown as React.UIEvent<HTMLDivElement>);
    };
    scrollRoot.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollRoot.removeEventListener('scroll', onScroll);
      bindScrollContainer(null);
    };
  }, [bindScrollContainer, collapsed, handleScroll]);

  const loadFacetCounts = useCallback(async () => {
    try {
      const res = await getAgentConversationFacets();
      const nextTree = buildHistoryMenuFacetTree(res?.data);
      setFacetTree(nextTree);
      setSelectedKey((previous) => {
        if (previous === ALL_FACET_KEY) {
          return previous;
        }
        return findFacetNodeByKey(nextTree, previous) ? previous : ALL_FACET_KEY;
      });
    } catch (error) {
      setFacetTree(buildHistoryMenuFacetTree());
    }
  }, []);

  useEffect(() => {
    loadFacetCounts();
  }, [loadFacetCounts]);

  useEffect(() => {
    return () => {
      clearStatusPolling();
    };
  }, [clearStatusPolling]);

  const applyLocalHistoryChange = useCallback(
    (change: { type: 'rename' | 'delete'; sessionId: string; title?: string }) => {
      const sessionId = change.sessionId?.trim();
      if (!sessionId) {
        return;
      }

      if (change.type === 'rename') {
        const nextTitle = change.title?.trim();
        if (!nextTitle) {
          return;
        }
        setItems((previous) => previous.map((item) => ((item.session_id || item.id) === sessionId ? { ...item, title: nextTitle } : item)));
        return;
      }

      pendingSessionIdsRef.current.delete(sessionId);
      setItems((previous) => previous.filter((item) => (item.session_id || item.id) !== sessionId));
    },
    [setItems],
  );

  const { activeItem, closeDeleteModal, closeRenameModal, deleteVisible, handleDeleteConfirm, handleRenameConfirm, mutating, openDeleteModal, openRenameModal, renameVisible } = useHistoryItemActions({
    source: 'sidebar',
    onActionSuccess: async (change) => {
      if (change.type === 'rename' || change.type === 'delete') {
        applyLocalHistoryChange(change);
        if (change.type === 'delete') {
          await loadFacetCounts();
        }
        return;
      }
      // await reload();
      // await loadFacetCounts();
    },
  });

  const syncSidebarHistory = useCallback(async () => {
    const lastChange = useHistoryConversationStore.getState().lastChange;
    if (!lastChange) {
      return;
    }

    if (lastChange.type === 'create' || lastChange.type === 'read' || lastChange.type === 'resume') {
      const sessionId = lastChange.sessionId?.trim();
      if (!sessionId) {
        return;
      }

      // create/read 都优先走增量 status 轮询，避免全量 reload
      enqueueStatusPolling([sessionId]);

      if (lastChange.type !== 'create') {
        return;
      }

      setItems((previous) => {
        const existed = previous.some((item) => (item.session_id || item.id) === sessionId);
        if (existed) {
          return previous;
        }

        const newItem: SessionHistoryItem = {
          id: sessionId,
          session_id: sessionId,
          title: '新项目',
          created_at: '',
          category: lastChange.source as any,
          status: '',
        };
        return [newItem, ...previous];
      });
      return;
    }

    if (lastChange.type === 'rename' || lastChange.type === 'delete') {
      applyLocalHistoryChange(lastChange);
      if (lastChange.type === 'delete') {
        await loadFacetCounts();
      }
      return;
    }

    // await reload();
  }, [applyLocalHistoryChange, enqueueStatusPolling, reload, setItems]);

  useHistoryConversationSync({
    source: 'sidebar',
    onSync: syncSidebarHistory,
  });

  if (collapsed) {
    return (
      <div ref={rootRef} className="session-history-menu-footer session-history-menu-footer-collapsed">
        <Tooltip title="项目">
          <Button
            className={`session-history-menu-footer-collapsed-button${isAllProjectsActive ? ' session-history-menu-footer-collapsed-button-active' : ''}`}
            type="text"
            icon={<ClockCircleOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              navigate('/session-history/all');
            }}
          />
        </Tooltip>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="session-history-menu-footer">
      <div className="session-history-menu-footer-header" onClick={() => setExpanded((prev) => !prev)}>
        <div className="session-history-menu-footer-header-left">
          <span className="session-history-menu-footer-header-title">项目</span>
        </div>
        <div className="session-history-menu-footer-header-actions">
          <Tooltip title="全部项目">
            <Button
              className={`session-history-menu-footer-header-action-button${isAllProjectsActive ? ' session-history-menu-footer-header-action-button-active' : ''}`}
              type="text"
              icon={<ExportOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                navigate('/session-history/all');
              }}
            />
          </Tooltip>
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            menu={{
              items: filterOptions,
              selectedKeys: [selectedKey],
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                setSelectedKey(key);
              },
            }}
          >
            <Tooltip title="筛选">
              <Button className={`session-history-menu-footer-header-action-button${selectedKey !== ALL_FACET_KEY ? ' session-history-menu-footer-header-action-button-active' : ''}`} type="text" icon={<FilterOutlined />} onClick={(event) => event.stopPropagation()} />
            </Tooltip>
          </Dropdown>
          {expanded ? <UpOutlined className="session-history-menu-footer-header-caret" /> : <DownOutlined className="session-history-menu-footer-header-caret" />}
        </div>
      </div>

      {expanded && (
        <>
          <Spin spinning={loading && items.length > 0} indicator={<LoadingOutlined className="session-history-menu-footer-loading-icon" spin />}>
            <div className="session-history-menu-footer-list">
              <HistoryList items={items} loading={loading && items.length === 0} loadingMore={loadingMore} hasMore={hasMore} onRename={openRenameModal} onDelete={openDeleteModal} emptyText="暂无历史会话" />
              {hasMore ? <div ref={loadMoreSentinelRef} className="session-history-menu-footer-load-more-sentinel" /> : null}
            </div>
          </Spin>
        </>
      )}

      <HistoryRenameModal visible={renameVisible} loading={mutating} item={activeItem} onConfirm={handleRenameConfirm} onCancel={closeRenameModal} />

      <HistoryDeleteModal visible={deleteVisible} loading={mutating} onConfirm={handleDeleteConfirm} onCancel={closeDeleteModal} />
    </div>
  );
};

export default HistoryMenuFooter;
